'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';

interface ActivitiesFormValues {
  sakashAmritvela: string;
  sakashNumaShaam: string;
  sakashNotes: string;

  awarenessJeevan: string;
  awarenessJeevanReach: string;
  awarenessVidyarthi: string;
  awarenessVidyarthiReach: string;
  awarenessNukad: string;
  awarenessMascot: string;
  awarenessNotes: string;

  walksCount: string;
  walksParticipants: string;
  walksType: string;
  walksDate: string;
  walksVIPs: string;
  walksNotes: string;

  cleanupCount: string;
  cleanupParticipants: string;
  cleanupDate: string;
  cleanupLocation: string;
  cleanupCorporates: string;
  cleanupNotes: string;

  competitionType: string;
  competitionDate: string;
  competitionParticipants: string;
  competitionTheme: string;
  competitionNotes: string;

  workshopCount: string;
  workshopTopic: string;
  workshopParticipants: string;
  workshopDate: string;
  seedBalls: string;
  workshopNotes: string;
}

interface ActivitiesTabProps {
  onChange: () => void;
  onSnapshot?: (key: string, value: string) => void;
}

const sectionClass = 'bg-card rounded-2xl border border-border shadow-card p-5 space-y-4';
const headerClass = 'flex items-center gap-3 pb-3 border-b border-border';

const competitionTypes = [
  'Nature Photography Contest',
  'Poster / Painting / Rangoli Competition',
  'Storytelling Competition for Children',
  'Combined Event',
];

const workshopTopics = [
  'Kitchen Gardening Workshop',
  'Living with Simplicity',
  'Spiritual Ecology',
  'Earth Care',
  'Opening the Heart',
  'Respecting Life',
  'Empowering Yourself',
  'Eating Well',
  'Personal Responsibility',
  'Consciousness and Energy',
  'The Power to Change Habits',
  'Say No to Plastic',
  'Water Conservation',
  'Organic Farming',
  'Sustainable Living',
  'Strengthening Resilience',
  'Satvik Rasoi Workshop',
  'Seed Ball Making',
];

export default function ActivitiesTab({ onChange, onSnapshot }: ActivitiesTabProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    sakash: true,
    awareness: true,
    walks: false,
    cleanup: false,
    competitions: false,
    workshops: false,
  });

  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const snap = (key: string, value: string) => {
    onChange();
    onSnapshot?.(key, value);
  };

  const { register, formState: { errors } } = useForm<ActivitiesFormValues>({
    defaultValues: {
      sakashAmritvela: '',
      sakashNumaShaam: '',
      awarenessJeevan: '0',
      awarenessVidyarthi: '0',
      walksCount: '0',
      cleanupCount: '0',
      workshopCount: '0',
    },
  });

  const SectionHeader = ({
    sectionKey, icon, iconBg, iconColor, title, subtitle,
  }: {
    sectionKey: string; icon: string; iconBg: string; iconColor: string; title: string; subtitle: string;
  }) => (
    <div className={headerClass}>
      <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon name={icon as any} size={18} variant="outline" className={iconColor} />
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-bold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
      </div>
      <button
        type="button"
        onClick={() => toggle(sectionKey)}
        className="p-1.5 rounded-lg hover:bg-muted transition-colors"
        aria-label={expanded[sectionKey] ? 'Collapse' : 'Expand'}
      >
        <Icon name={expanded[sectionKey] ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} variant="outline" className="text-muted-foreground" />
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* 1. Sakash */}
      <div className={sectionClass}>
        <SectionHeader
          sectionKey="sakash"
          icon="SparklesIcon"
          iconBg="bg-secondary/10"
          iconColor="text-secondary"
          title="1. Collective Meditation — Sakash to Mother Earth 🧘‍♀️"
          subtitle="Daily Rajyog meditation for healing the 5 elements"
        />
        {expanded.sakash && (
          <div className="space-y-4 animate-fade-in">
            <div className="p-3 bg-secondary/5 rounded-xl border border-secondary/20">
              <p className="text-xs text-secondary font-semibold mb-1">Daily Practice</p>
              <p className="text-xs text-muted-foreground">
                5 minutes at end of Amritvela + 5 minutes at end of Numa Shaam yog — sending positive vibrations to the body of 5 elements.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="sakash-amritvela">
                  Amritvela Sessions Held This Month
                  <span className="text-[var(--danger)] ml-0.5">*</span>
                </label>
                <p className="form-helper">Days when Amritvela Sakash was conducted (max 30/31)</p>
                <input
                  id="sakash-amritvela"
                  type="number"
                  min="0"
                  max="31"
                  placeholder="e.g. 28"
                  className={`form-input mt-1 ${errors.sakashAmritvela ? 'form-input-error' : ''}`}
                  {...register('sakashAmritvela', {
                    required: 'Amritvela session count is required',
                    min: { value: 0, message: 'Cannot be negative' },
                    max: { value: 31, message: 'Cannot exceed 31 days' },
                  })}
                  onChange={(e) => snap('sakashAmritvela', e.target.value)}
                />
                {errors.sakashAmritvela && <p className="form-error">{errors.sakashAmritvela.message}</p>}
              </div>
              <div>
                <label className="form-label" htmlFor="sakash-numa">
                  Numa Shaam Sessions Held This Month
                  <span className="text-[var(--danger)] ml-0.5">*</span>
                </label>
                <p className="form-helper">Days when Numa Shaam Sakash was conducted (max 30/31)</p>
                <input
                  id="sakash-numa"
                  type="number"
                  min="0"
                  max="31"
                  placeholder="e.g. 26"
                  className={`form-input mt-1 ${errors.sakashNumaShaam ? 'form-input-error' : ''}`}
                  {...register('sakashNumaShaam', {
                    required: 'Numa Shaam session count is required',
                    min: { value: 0, message: 'Cannot be negative' },
                    max: { value: 31, message: 'Cannot exceed 31 days' },
                  })}
                  onChange={(e) => snap('sakashNumaShaam', e.target.value)}
                />
                {errors.sakashNumaShaam && <p className="form-error">{errors.sakashNumaShaam.message}</p>}
              </div>
            </div>
            <div>
              <label className="form-label" htmlFor="sakash-notes">Notes on Collective Meditation Experience</label>
              <textarea
                id="sakash-notes"
                rows={2}
                placeholder="Observations, collective experience, number of participants..."
                className="form-input resize-none"
                {...register('sakashNotes')}
                onChange={onChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* 2. Awareness Sessions */}
      <div className={sectionClass}>
        <SectionHeader
          sectionKey="awareness"
          icon="AcademicCapIcon"
          iconBg="bg-[var(--info-bg)]"
          iconColor="text-[var(--info)]"
          title="2. Awareness Sessions 📝"
          subtitle="Jeevanshaili Parivartan + Vidyarthi Parivartan + Nukad Nataks"
        />
        {expanded.awareness && (
          <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="awareness-jeevan-count">Jeevanshaili Parivartan Sessions</label>
                <p className="form-helper">BK Student awareness sessions on water, waste, energy, food</p>
                <input
                  id="awareness-jeevan-count"
                  type="number"
                  min="0"
                  placeholder="0"
                  className="form-input mt-1"
                  {...register('awarenessJeevan')}
                  onChange={(e) => snap('awarenessJeevan', e.target.value)}
                />
              </div>
              <div>
                <label className="form-label" htmlFor="awareness-jeevan-reach">Jeevanshaili Reach (People)</label>
                <p className="form-helper">Total BK students reached via Jeevanshaili sessions</p>
                <input
                  id="awareness-jeevan-reach"
                  type="number"
                  min="0"
                  placeholder="0"
                  className="form-input mt-1"
                  {...register('awarenessJeevanReach')}
                  onChange={onChange}
                />
              </div>
              <div>
                <label className="form-label" htmlFor="awareness-vidyarthi-count">Vidyarthi Parivartan Sessions</label>
                <p className="form-helper">Sessions in schools, colleges, and community areas</p>
                <input
                  id="awareness-vidyarthi-count"
                  type="number"
                  min="0"
                  placeholder="0"
                  className="form-input mt-1"
                  {...register('awarenessVidyarthi')}
                  onChange={(e) => snap('awarenessVidyarthi', e.target.value)}
                />
              </div>
              <div>
                <label className="form-label" htmlFor="awareness-vidyarthi-reach">Vidyarthi Reach (People)</label>
                <p className="form-helper">Total students and community members reached</p>
                <input
                  id="awareness-vidyarthi-reach"
                  type="number"
                  min="0"
                  placeholder="0"
                  className="form-input mt-1"
                  {...register('awarenessVidyarthiReach')}
                  onChange={onChange}
                />
              </div>
              <div>
                <label className="form-label" htmlFor="awareness-nukad">Nukad Nataks Performed</label>
                <p className="form-helper">Street plays spreading eco-friendly lifestyle awareness</p>
                <input
                  id="awareness-nukad"
                  type="number"
                  min="0"
                  placeholder="0"
                  className="form-input mt-1"
                  {...register('awarenessNukad')}
                  onChange={onChange}
                />
              </div>
              <div>
                <label className="form-label" htmlFor="awareness-mascot">Mascot Door-to-Door Visits</label>
                <p className="form-helper">Number of households/areas visited by mascot team</p>
                <input
                  id="awareness-mascot"
                  type="number"
                  min="0"
                  placeholder="0"
                  className="form-input mt-1"
                  {...register('awarenessMascot')}
                  onChange={onChange}
                />
              </div>
            </div>
            <div>
              <label className="form-label" htmlFor="awareness-notes">Session Notes & Themes Covered</label>
              <textarea
                id="awareness-notes"
                rows={2}
                placeholder="Key themes: Save Energy, Reduce Plastic, Save Water, Renewable Energy, E-Waste..."
                className="form-input resize-none"
                {...register('awarenessNotes')}
                onChange={onChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* 3. Walks With Nature */}
      <div className={sectionClass}>
        <SectionHeader
          sectionKey="walks"
          icon="UserGroupIcon"
          iconBg="bg-secondary/10"
          iconColor="text-secondary"
          title="3. Walks With Nature 🚶‍♂️"
          subtitle="Peace walks, yoga runs, mini marathons for environment"
        />
        {expanded.walks && (
          <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="walks-count">Number of Walk Events</label>
                <input
                  id="walks-count"
                  type="number"
                  min="0"
                  placeholder="0"
                  className="form-input"
                  {...register('walksCount')}
                  onChange={(e) => snap('walksCount', e.target.value)}
                />
              </div>
              <div>
                <label className="form-label" htmlFor="walks-participants">Total Participants</label>
                <input
                  id="walks-participants"
                  type="number"
                  min="0"
                  placeholder="0"
                  className="form-input"
                  {...register('walksParticipants')}
                  onChange={onChange}
                />
              </div>
              <div>
                <label className="form-label" htmlFor="walks-type">Type of Walk</label>
                <input
                  id="walks-type"
                  type="text"
                  placeholder="e.g. Peace Walk, Yoga Run, Mini Marathon"
                  className="form-input"
                  {...register('walksType')}
                  onChange={onChange}
                />
              </div>
              <div>
                <label className="form-label" htmlFor="walks-date">Date of Walk</label>
                <input
                  id="walks-date"
                  type="date"
                  className="form-input"
                  {...register('walksDate')}
                  onChange={onChange}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="form-label" htmlFor="walks-vips">VIP / Local Leader Invitees</label>
                <p className="form-helper">Names of local VIPs invited to sensitise them towards environment</p>
                <input
                  id="walks-vips"
                  type="text"
                  placeholder="e.g. Corporator Ramesh Patil, Principal Dr. Meena Joshi"
                  className="form-input mt-1"
                  {...register('walksVIPs')}
                  onChange={onChange}
                />
              </div>
            </div>
            <div>
              <label className="form-label" htmlFor="walks-notes">Walk Notes & Key Slogans Used</label>
              <textarea
                id="walks-notes"
                rows={2}
                placeholder="Slogans used, media coverage, community response..."
                className="form-input resize-none"
                {...register('walksNotes')}
                onChange={onChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* 4. Community Clean-ups */}
      <div className={sectionClass}>
        <SectionHeader
          sectionKey="cleanup"
          icon="TrashIcon"
          iconBg="bg-[var(--warning-bg)]"
          iconColor="text-[var(--warning)]"
          title="4. Community Clean-ups"
          subtitle="Gardens, parks, roads — Earth Day, World Clean Up Day"
        />
        {expanded.cleanup && (
          <div className="space-y-4 animate-fade-in">
            <div className="p-3 bg-[var(--warning-bg)] rounded-xl border border-[var(--warning)]/20">
              <p className="text-xs font-semibold text-[var(--warning)] mb-1">Key Clean-up Days 2026</p>
              <div className="flex flex-wrap gap-2">
                {['Earth Day — 22 Apr', 'World Clean Up Day — 20 Sep'].map((d) => (
                  <span key={`cleanup-day-${d}`} className="text-[10px] font-medium bg-white text-[var(--warning)] px-2 py-0.5 rounded-full border border-[var(--warning)]/30">
                    {d}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="cleanup-count">Number of Clean-up Events</label>
                <input id="cleanup-count" type="number" min="0" placeholder="0" className="form-input" {...register('cleanupCount')} onChange={(e) => snap('cleanupCount', e.target.value)} />
              </div>
              <div>
                <label className="form-label" htmlFor="cleanup-participants">Total Participants</label>
                <p className="form-helper">BK students + community members + corporates combined</p>
                <input id="cleanup-participants" type="number" min="0" placeholder="0" className="form-input mt-1" {...register('cleanupParticipants')} onChange={onChange} />
              </div>
              <div>
                <label className="form-label" htmlFor="cleanup-date">Date of Main Clean-up Event</label>
                <input id="cleanup-date" type="date" className="form-input" {...register('cleanupDate')} onChange={onChange} />
              </div>
              <div>
                <label className="form-label" htmlFor="cleanup-location">Location Cleaned</label>
                <input id="cleanup-location" type="text" placeholder="e.g. Shivajinagar Garden, Pune" className="form-input" {...register('cleanupLocation')} onChange={onChange} />
              </div>
              <div className="sm:col-span-2">
                <label className="form-label" htmlFor="cleanup-corporates">Corporate Partners Involved</label>
                <input id="cleanup-corporates" type="text" placeholder="e.g. TCS Pune, Infosys BPO" className="form-input" {...register('cleanupCorporates')} onChange={onChange} />
              </div>
            </div>
            <div>
              <label className="form-label" htmlFor="cleanup-notes">Clean-up Notes</label>
              <textarea id="cleanup-notes" rows={2} placeholder="Areas covered, waste collected (kg), team competition results..." className="form-input resize-none" {...register('cleanupNotes')} onChange={onChange} />
            </div>
          </div>
        )}
      </div>

      {/* 5. Competitions */}
      <div className={sectionClass}>
        <SectionHeader
          sectionKey="competitions"
          icon="PhotoIcon"
          iconBg="bg-purple-50"
          iconColor="text-purple-600"
          title="5. Competitions 🎨"
          subtitle="Photography, Poster, Painting, Rangoli, Storytelling"
        />
        {expanded.competitions && (
          <div className="space-y-4 animate-fade-in">
            <div className="p-3 bg-purple-50 rounded-xl border border-purple-100">
              <p className="text-xs font-semibold text-purple-700 mb-2">Upcoming Competition Days 2026</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  'World Water Day — 22 Mar',
                  'Earth Hour — 28 Mar',
                  'Zero Waste Day — 30 Mar',
                  'Earth Day — 22 Apr',
                  'World Environment Day — 5 Jun',
                  'World Vegetarian Day — 1 Oct',
                  'International E-Waste Day — 14 Oct',
                ].map((d) => (
                  <span key={`comp-day-${d}`} className="text-[10px] font-medium bg-white text-purple-600 px-2 py-0.5 rounded-full border border-purple-200">
                    {d}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="comp-type">Competition Type</label>
                <div className="relative">
                  <select id="comp-type" className="form-input appearance-none pr-8" {...register('competitionType')} onChange={(e) => snap('competitionType', e.target.value)}>
                    <option value="">Select type</option>
                    {competitionTypes.map((t) => (
                      <option key={`comp-type-${t}`} value={t}>{t}</option>
                    ))}
                  </select>
                  <Icon name="ChevronDownIcon" size={14} variant="outline" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="form-label" htmlFor="comp-date">Competition Date</label>
                <input id="comp-date" type="date" className="form-input" {...register('competitionDate')} onChange={onChange} />
              </div>
              <div>
                <label className="form-label" htmlFor="comp-participants">Number of Participants</label>
                <input id="comp-participants" type="number" min="0" placeholder="0" className="form-input" {...register('competitionParticipants')} onChange={onChange} />
              </div>
              <div>
                <label className="form-label" htmlFor="comp-theme">Theme / Topic</label>
                <input id="comp-theme" type="text" placeholder="e.g. Respect Mother Earth, Green Planet" className="form-input" {...register('competitionTheme')} onChange={onChange} />
              </div>
            </div>
            <div>
              <label className="form-label" htmlFor="comp-notes">Competition Notes</label>
              <textarea id="comp-notes" rows={2} placeholder="Winners, notable entries, community response, prizes given..." className="form-input resize-none" {...register('competitionNotes')} onChange={onChange} />
            </div>
          </div>
        )}
      </div>

      {/* 6. Eco-Friendly Workshops */}
      <div className={sectionClass}>
        <SectionHeader
          sectionKey="workshops"
          icon="BeakerIcon"
          iconBg="bg-primary/10"
          iconColor="text-primary"
          title="6. Eco-Friendly Lifestyle Dialogues / Workshops 🌍"
          subtitle="Seed ball making, kitchen gardening, spiritual ecology, and more"
        />
        {expanded.workshops && (
          <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="workshop-count">Total Workshop Sessions</label>
                <input id="workshop-count" type="number" min="0" placeholder="0" className="form-input" {...register('workshopCount')} onChange={(e) => snap('workshopCount', e.target.value)} />
              </div>
              <div>
                <label className="form-label" htmlFor="workshop-participants">Total Participants</label>
                <input id="workshop-participants" type="number" min="0" placeholder="0" className="form-input" {...register('workshopParticipants')} onChange={onChange} />
              </div>
              <div>
                <label className="form-label" htmlFor="workshop-topic">Primary Workshop Topic</label>
                <div className="relative">
                  <select id="workshop-topic" className="form-input appearance-none pr-8" {...register('workshopTopic')} onChange={onChange}>
                    <option value="">Select topic</option>
                    {workshopTopics.map((t) => (
                      <option key={`ws-topic-${t}`} value={t}>{t}</option>
                    ))}
                  </select>
                  <Icon name="ChevronDownIcon" size={14} variant="outline" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="form-label" htmlFor="workshop-date">Date of Workshop</label>
                <input id="workshop-date" type="date" className="form-input" {...register('workshopDate')} onChange={onChange} />
              </div>
              <div className="sm:col-span-2">
                <label className="form-label" htmlFor="workshop-seedballs">Seed Balls Made (count)</label>
                <p className="form-helper">Number of seed balls prepared and distributed during the activity</p>
                <input id="workshop-seedballs" type="number" min="0" placeholder="0" className="form-input mt-1" {...register('seedBalls')} onChange={onChange} />
              </div>
            </div>
            <div>
              <label className="form-label" htmlFor="workshop-notes">Workshop Notes</label>
              <textarea id="workshop-notes" rows={2} placeholder="Facilitator name, key learnings, participant feedback, follow-up planned..." className="form-input resize-none" {...register('workshopNotes')} onChange={onChange} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}